import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { usePartnershipData } from './usePartnershipData.jsx';

export const W3ProjectsChart = memo(function W3ProjectsChart({ results: resultsProp }) {
  const { w3BilateralResults } = usePartnershipData();
  const yAxisWidth = useResponsiveYAxisWidth(200);
  const results = resultsProp ?? w3BilateralResults;

  const noDataFallback = (
    <ChartWrapper title="W3 Associated With Reported Results">
      <NoChartData message="No W3/Bilateral projects found" detail="This chart shows W3/Bilateral projects and their result counts. No results with W3/Bilateral project data were found in the current selection." />
    </ChartWrapper>
  );

  if (!results || results.length === 0) return noDataFallback;

  const projectCounts = {};
  results.forEach(r => {
    if (r.w3ProjectTitle) {
      projectCounts[r.w3ProjectTitle] = (projectCounts[r.w3ProjectTitle] || 0) + 1;
    }
  });

  const chartData = Object.entries(projectCounts)
    .map(([name, count]) => ({
      name: name.length > 35 ? name.substring(0, 33) + '...' : name,
      fullName: name,
      results: count,
    }))
    .sort((a, b) => b.results - a.results)
    .slice(0, 15);

  if (chartData.length === 0) return noDataFallback;

  return (
    <ChartWrapper title="W3 Associated With Reported Results" info="W3/Bilateral projects ranked by number of results reported under each grant." chartData={chartData} sourceResults={results} exportFilename="w3-projects">
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 200 ? 9 : 10 }} width={yAxisWidth} />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{d.fullName}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F37021', display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ color: '#4b5563' }}>Results: <strong>{d.results?.toLocaleString()}</strong></span>
                </div>
              </div>
            );
          }} />
          <Bar dataKey="results" fill="#F37021" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="results" position="right" {...LABEL_STYLE} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
